import React from 'react';

import { useDate, DateContext } from './DateContext';

export interface DateProviderProps {
  /** Client's timezone */
  timezone?: string;
}

/**
 * A provider of common Date info to the app that needs to be directly
 * shared to components
 *
 * When components use the DateProvider they will be able to work inside
 * or outside of the provider since wrapping the component in a provider
 * is just overriding the defaultContext.
 *
 * For Example `DateTime`:
 *
 * This works outside of the DateProvider and falls back to browser timezone
 * ```
 * <DateTime />
 * ```
 *
 * However to get the correct user profile timezone we must wrap it
 * inside of an DateProvider
 *
 * ```
 * <DateProvider timezone="EST">
 *  <DateTime />
 * </DateProvider>
 * ```
 *
 * This is useful because we can:
 *
 * 1. Create / test the components independent of the data source here.
 *
 * 2. Connect them to data sources based on the environment of the app.
 * <br />- `web` will connect DateProvider to the redux store
 * <br />- `web-ssr` will use graphql queries in the DateProvider
 */
const DateProvider: React.FC<DateProviderProps> = props => {
  const { children, ...innerDate } = props;
  const outerDate = useDate();

  const app = { ...outerDate, ...innerDate };

  return children ? (
    <DateContext.Provider value={app}>
      {React.Children.only(children)}
    </DateContext.Provider>
  ) : null;
};

export default DateProvider;
